// useTabs.tsx

import { useState, useCallback, useMemo } from 'react'

export interface TabItem {
  id: string
  label: string
  icon?: React.ReactElement
  component?: React.LazyExoticComponent<React.ComponentType<unknown>> | React.ComponentType<unknown>
  children?: TabItem[]
}

export type TabsConfig = Record<string, TabItem>

export type LazyComponent = React.LazyExoticComponent<React.ComponentType<unknown>> | React.ComponentType<unknown>

function getDefaultPath(config: TabsConfig): string[] {
  const path: string[] = []
  const firstKey = Object.keys(config)[0]
  if (!firstKey) return path

  path.push(firstKey)
  let current: TabItem | undefined = config[firstKey]

  while (current?.children?.[0]) {
    path.push(current.children[0].id)
    current = current.children[0]
  }

  return path
}

function getChildDefaults(item: TabItem | undefined): string[] {
  const path: string[] = []
  let current: TabItem | undefined = item

  while (current?.children?.[0]) {
    path.push(current.children[0].id)
    current = current.children[0]
  }

  return path
}

function getItemAtPath(config: TabsConfig, path: string[]): TabItem | undefined {
  if (path.length === 0) return undefined

  let current: TabItem | undefined = config[path[0]]
  for (let i = 1; i < path.length; i++) {
    if (!current?.children) return undefined
    current = current.children.find((c) => c.id === path[i])
    if (!current) return undefined
  }

  return current
}

export function useTabs(config: TabsConfig) {
  const keys = useMemo(() => Object.keys(config), [config])

  const [ids, setIds] = useState<string[]>(() => getDefaultPath(config))

  const current = useMemo((): (TabItem | null)[] => {
    const result: (TabItem | null)[] = []

    const l0 = config[ids[0]] ?? null
    result.push(l0)

    let parent: TabItem | null = l0
    for (let i = 1; i < ids.length; i++) {
      if (!parent?.children) {
        result.push(null)
        parent = null
        continue
      }
      const item = parent.children.find((c) => c.id === ids[i]) ?? null
      result.push(item)
      parent = item
    }

    return result
  }, [config, ids])

  const index = useMemo((): number[] => {
    const result: number[] = []

    const i0 = keys.indexOf(ids[0])
    result.push(i0 >= 0 ? i0 : 0)

    for (let i = 1; i < ids.length; i++) {
      const parent = current[i - 1]
      const idx = parent?.children?.findIndex((c) => c.id === ids[i]) ?? 0
      result.push(idx >= 0 ? idx : 0)
    }

    return result
  }, [keys, ids, current])

  const select = useCallback((level: number, id: string) => {
    setIds((prev) => {
      const newPath = prev.slice(0, level)
      newPath[level] = id

      const item = getItemAtPath(config, newPath)
      return [...newPath, ...getChildDefaults(item)]
    })
  }, [config])

  const goTo = useCallback((...path: string[]) => {
    if (path.length === 0) return

    const newIds: string[] = []
    let currentItem: TabItem | undefined = config[path[0]]

    if (!currentItem) return

    newIds.push(path[0])

    for (let i = 1; i < path.length; i++) {
      if (!currentItem) break
      const child = currentItem.children?.find((c) => c.id === path[i])
      if (!child) break
      newIds.push(path[i])
      currentItem = child
    }

    setIds([...newIds, ...getChildDefaults(currentItem)])
  }, [config])

  const onChange = useMemo(() => {
    const handlers: ((e: React.SyntheticEvent, idx: number) => void)[] = []

    handlers.push((_, i) => {
      const id = keys[i]
      if (id) select(0, id)
    })

    for (let level = 1; level <= 10; level++) {
      const lvl = level
      handlers.push((_, i) => {
        const parent = current[lvl - 1]
        const id = parent?.children?.[i]?.id
        if (id) select(lvl, id)
      })
    }

    return handlers
  }, [keys, current, select])

  const Component = useMemo((): LazyComponent | null => {
    for (let i = current.length - 1; i >= 0; i--) {
      const item = current[i]
      if (item?.component) {
        return item.component
      }
    }
    return null
  }, [current])

  return {
    keys,
    current,
    index,
    onChange,
    ids,
    select,
    goTo,
    Component,
  }
}